import { Component, ElementRef, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Grid, GridSearchItem } from '../../../../../shared/Models/grid';
import { ActionColumn, ActionItem, Column, TextColumn } from '../../../../../shared/Models/columns';
import { ExchangeService } from '../../../../Services/exchange.service';
import { AuthenticationService } from '../../../../../shared/Services/authentication.service';
import { IButton } from '../../../../../shared/card/card.component';
import { TranslateService } from '@ngx-translate/core';
import { Helper } from '../../../../../helpers/helper.service';
import { environment } from '../../../../../../environments/environment';
import * as moment from 'moment';
import { UsersService } from 'src/app/back-office/Services/users.service';
import { HttpParams } from '@angular/common/http';
import { BroadcastService } from 'src/app/shared/Services/broadcast.service';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-backoffice-exchange-overview',
  templateUrl: './backoffice-exchange-overview.component.html',
  styleUrls: ['./backoffice-exchange-overview.component.scss'],
})
export class BackofficeExchangeOverviewComponent implements OnInit {
  grid: Grid;
  public specialButton: IButton[] = [];
  public params = new HttpParams();
  @ViewChild('downloadLink', { static: false }) downloadLink: ElementRef;
  @ViewChild('uploadModal', { static: false }) uploadModal;
  @ViewChild('importResultModal', { static: false }) importResultModal;
  @ViewChild('importSuccessModal', { static: false }) importSuccessModal;
  @ViewChild('cellEllipsisTooltip', { static: true }) cellEllipsisTooltip!: TemplateRef<unknown>;
  @ViewChild('orderNoLinkTpl', { static: true }) orderNoLinkTpl!: TemplateRef<unknown>;
  @ViewChild('uploaderFileInput', { static: false }) uploaderFileInput?: ElementRef<HTMLInputElement>;

  public isUploading = false;
  public messages: string[] = [];
  public importSuccessRows: Array<{ row: string; changedCellsLabel: string }> = [];
  selectedFile: File | null = null;
  selectedFileName = '';
  isBackOffice = signal(false)

  formatImportMessage(message: string): string {
    const m = String(message ?? '');
    if (!m) return m;

    return m.replace(/\s*\brow\b/gi, (match, offset) => {
      const matchTrimStart = match.replace(/^\s+/, '');
      if (offset === 0) return matchTrimStart;
      const prev = m[offset - 1];
      if (prev === '\n') return matchTrimStart;
      return `\n${matchTrimStart}`;
    });
  }

  constructor(
    private readonly router: Router,
    private readonly exchangeService: ExchangeService,
    private readonly auth: AuthenticationService,
    private readonly translator: TranslateService,
    private readonly helper: Helper,
    private userService: UsersService,
    private broadcastService: BroadcastService,
  ) {
    this.isBackOffice.set(this.auth.isBackOffice())
  }

  private sanitizeGridParams(p: HttpParams): HttpParams {
    let next = ['q[invoiceStatus]', 'q[status]', 'q[collection_id]', 'q[dealer_id]', 'q[model_id]'].reduce(
      (acc, key) => acc.delete(key),
      p
    );


    const filterRaw = next.get('filter');
    if (filterRaw) {
      try {
        const decoded = decodeURIComponent(filterRaw);
        const filterObj: any = JSON.parse(decoded);
        if (filterObj && typeof filterObj === 'object' && !Array.isArray(filterObj)) {
          const mapped: any = {};
          for (const k of Object.keys(filterObj)) {
            let nk = k;
            if (k === 'order.supplier') nk = 'supplier';
            if (k === 'order.realDealer' || k.startsWith('order.realDealer')) nk = 'realDealer';
            mapped[nk] = filterObj[k];
          }
          next = next.set('filter', encodeURIComponent(JSON.stringify(mapped)));
        }
      } catch {
      }
    }

    return next;
  }

  cellDisplay(column: TextColumn, value: unknown): { text: string; showTooltip: boolean } {
    let text: string;
    if (column.valueFormatter) {
      const fmt = column.valueFormatter(value as never);
      text = fmt === null || fmt === undefined || fmt === '' ? '-' : String(fmt);
    } else if (value === null || value === undefined || value === '') {
      text = '-';
    } else if (typeof value === 'object') {
      const o = value as Record<string, unknown>;
      const name = o['companyName'] ?? o['name'];
      text = name !== undefined && name !== null && String(name).trim() !== '' ? String(name) : '-';
    } else {
      text = String(value);
    }
    const trimmed = text.trim();
    const showTooltip = trimmed.length > 0 && trimmed !== '-';
    return { text, showTooltip };
  }

  getExchanges(gridData: HttpParams = new HttpParams(), _params: HttpParams = new HttpParams()): Observable<any> {
    return this.exchangeService.getExchanges(this.sanitizeGridParams(gridData), _params);
  }

  private canUseDealerAndSupplierFilters(): boolean {
    return (
      this.auth.isBackofficeAdmin() ||
      this.auth.isBackofficeSuperadmin() ||
      this.auth.isBackOffice()
    );
  }

  ngOnInit(): void {
    const dateFormat = this.auth.getDateFormat();
    const canUseDealerAndSupplierFilters = this.canUseDealerAndSupplierFilters();

    const columns: Column[] = [
        <TextColumn>{
          name: 'exchange.created',
          prop: 'createdAt',
          sortable: true,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
          valueFormatter: row => moment(row).format(dateFormat),
        },
        <TextColumn>{
          name: 'exchange.service_number',
          prop: 'serviceNumber',
          sortable: true,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
          minWidth: 90,
          maxWidth: 200,
        },
        <TextColumn>{
          name: 'doctrine.order.number',
          prop: 'order.orderNo',
          sortable: true,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.orderNoLinkTpl,
          minWidth: 90,
          maxWidth: 200,
        },
    ];

    if (canUseDealerAndSupplierFilters) {
      columns.push(
        <TextColumn>{
          name: 'doctrine.order.dealer',
          prop: 'order',
          sortable: true,
          type: TextColumn.type,
          minWidth: 113,
          maxWidth: 200,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
          filterable: true,
          filterItems: this.getDealers(),
          filterMultiSelect: true,
          valueFormatter: row => row?.realDealer.fullName
        },
      );
    }

    columns.push(
        <TextColumn>{
          name: 'exchange.customerName',
          prop: 'customerName',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
          minWidth: 113,
          maxWidth: 200,
        },
        <TextColumn>{
          name: 'exchange.customerPhone',
          prop: 'customerPhone',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
          minWidth: 113,
          maxWidth: 200,
        },
        <TextColumn>{
          name: 'exchange.dealerNote',
          prop: 'dealerNote',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
        },
    );

    // Logistic note is hidden for dealer-system; only back-office sees it.
    if (this.auth.isBackOffice()) {
      columns.push(
        <TextColumn>{
          name: 'exchange.logisticNote',
          prop: 'logisticNote',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
        },
      );
    }

    columns.push(
        <TextColumn>{
          name: 'exchange.status',
          prop: 'exhangeStatus',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
        },
        <TextColumn>{
          name: 'exchange.physical_delivery_date',
          prop: 'physicalDeliveryAt',
          sortable: false,
          type: TextColumn.type,
          translate: true,
          noTruncate: true,
          valueTemplate: this.cellEllipsisTooltip,
          minWidth: 120,
          maxWidth: 200,
          valueFormatter: row => row ? moment(row).format(dateFormat) : '-',
        },
        <ActionColumn>{
          name: 'global.actions',
          prop: null,
          type: ActionColumn.type,
          sortable: false,
          translate: true,
          width: 110,
          minWidth: 110,
          maxWidth: 140,
          actionItems: [
            <ActionItem>{
              icon: 'fa fa-eye fa-lg',
              tooltip: 'global.view',
              onclick: (row) => {
                const exchangeId = row?.id  ?? null;
                if (exchangeId) {
                  if(this.auth.isBackOffice()) return this.router.navigateByUrl('back-office/exchange/' + exchangeId + '/edit');
                  else return this.router.navigateByUrl('dealer-system/exchange/' + exchangeId + '/edit');
                }
                return;
              },
              showCallback: (row) => !!(row?.id),
              btnClass: 'ripple light',
            },
          ],
        },
    );

    this.grid = <Grid>{
      columns,
      service: this,
      rememberURL: this.router.url,
      method: 'getExchanges',
      root: 'exchange',
      searchPlaceholder: 'search.about.order.no',
      defaultSort: 'id',
      defaultSortType: 'desc',
      showButton: true,
      doseNotHaveSearch: true,
      multipleRowSearch: false,
      search: [
        <GridSearchItem>{
          calendarFromDate: 'from',
          calendarToDate: 'to',
          hasCalendar: true,
          type: 'ng-select',
          hasDropdownListFilter: false,
          hasStatus: false,
          hasNormalSearch: true,
          order: [
            {actionOrder: 1, datepickerOrder: 2, normalSearchOrder: 3}
          ],
        }
      ],
    };

    this.translator.get(['exchange.export', 'exchange.import']).subscribe((t) => {
      const specialButtonArray = [
        {
          name: t['exchange.export'],
          btnIcon: 'feather icon-download',
          method: 'exportExchanges',
          customClass: 'btn-grey',
        },
        {
          name: t['exchange.import'],
          btnIcon: 'feather icon-upload',
          method: 'importDeliveryWeeks',
          customClass: 'btn-grey',
        },
      ];

      this.specialButton = specialButtonArray.map((items) => ({
        btnIcon: items.btnIcon,
        btnName: items.name,
        method: items.method,
        btnCustomClass: items.customClass,
      }));
    });
  }


  goToBackOfficeOrder(row: any): void {
    const orderId = row?.order?.id ?? null;
    if (!orderId) return;
    const id = encodeURIComponent(String(orderId));
    if (this.auth.isBackOffice()) {
      this.router.navigateByUrl('back-office/order/' + id);
    } else {
      this.router.navigateByUrl('dealer-system/order/' + id + '/show');
    }
  }

  calFunction(method: string) {
    if (typeof this[method] === 'function') this[method]();
  }

  setParams(event: HttpParams) {
    this.params = this.sanitizeGridParams(event);
  }

  exportExchanges() {
    this.exchangeService.exportExchange(this.params).subscribe((res) => {
      if (res?.path && res?.name && this.downloadLink?.nativeElement) {
        const el: HTMLElement = this.downloadLink.nativeElement as HTMLElement;
        el.setAttribute('download', 'download');
        el.setAttribute('href', environment.apiUrl + '/download?path=' + res.path + '&filename=' + res.name);
        el.click();
        return;
      }
      if (res?.file?.publicPath && res?.file?.fileName && this.downloadLink?.nativeElement) {
        const el: HTMLElement = this.downloadLink.nativeElement as HTMLElement;
        el.setAttribute('download', 'download');
        el.setAttribute('href', environment.apiUrl + '/download?path=' + res.file.publicPath + '&filename=' + res.file.fileName);
        el.click();
        return;
      }
      this.helper.popMessageDialog(
        this.translator.instant('global.success_message.title'),
        this.translator.instant('global.success_message.message'),
        'success',
        false
      );
    });
  }

  importDeliveryWeeks() {
    this.uploadModal.show();
  }

  getDealers() {
    const options = [];
    this.userService.getAllDealerUsersDropdown({sortType: 'ASC', sortBy: 'companyName'}).subscribe((res) => {
      for (const row of res) {
        options.push({value: row.id, label: row.companyName});
      }
    });
    return options;
  }

  onFileSelected(evt: Event) {
    const input = evt.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    this.selectedFile = file;
    this.selectedFileName = file?.name ?? '';

    if (input) input.value = '';

    if (!file) return;

    const name = String(file.name || '').toLowerCase();
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');
    if (!isExcel) {
      this.selectedFile = null;
      this.selectedFileName = '';
      this.resetUploaderInput();
      this.helper.popMessageDialog(
        this.translator.instant('global.warning'),
        'Please select an Excel file (.xlsx or .xls).',
        'warning',
        false
      );
    }
  }

  private resetUploaderInput(): void {
    const el = this.uploaderFileInput?.nativeElement;
    if (el) el.value = '';
  }

  private buildImportSuccessRows(objects: unknown): Array<{ row: string; changedCellsLabel: string }> {
    if (!Array.isArray(objects)) return [];
    return objects
      .map((o) => {
        const row = (o as any)?.row;
        const changedCells = (o as any)?.changedCells;
        const rowStr = row === null || row === undefined ? '' : String(row).trim();
        const cells = Array.isArray(changedCells)
          ? changedCells.map((c) => String(c)).filter((c) => c.trim() !== '')
          : [];
        return {
          row: rowStr,
          changedCellsLabel: cells.join(', ')
        };
      })
      .filter((x) => x.row !== '' || x.changedCellsLabel !== '');
  }

  uploadFile() {
    if (!this.selectedFile) return;
    this.isUploading = true;

    this.exchangeService.importExchange(this.selectedFile).subscribe(
      (res) => {
        this.uploadModal.hide();
        this.isUploading = false;
        this.messages = [];
        this.importSuccessRows = [];

        const payload = res?.exchanges ?? res?.orders ?? res;
        const errors = payload?.errors ?? res?.errors;
        const objects = payload?.objects ?? payload?.exchanges?.objects ?? res?.objects;

        const errorMessages = Array.isArray(errors)
          ? errors.map((e) => String(e)).filter((e) => !!e)
          : [];

        if (errorMessages.length === 0) {
          this.importSuccessRows = this.buildImportSuccessRows(objects);
          this.selectedFile = null;
          this.selectedFileName = '';
          this.resetUploaderInput();
          if (this.importSuccessRows.length > 0) {
            this.importSuccessModal.show();
          } else {
            this.helper.popMessageDialog(
              this.translator.instant('global.success_message.title'),
              this.translator.instant('global.success_message.message'),
              'success',
              false
            );
          }
          this.broadcastService.broadcast('refreshGrid', 'getExchanges');
          return;
        }

        errorMessages.forEach((m) => this.messages.push(this.formatImportMessage(m)));
        this.importResultModal.show();
      },
      (err) => {
        if (err?.status === 401) {
          return this.handle401Error();
        }
        this.isUploading = false;
      }
    );
  }

  cancelUpload() {
    this.selectedFile = null;
    this.selectedFileName = '';
    this.resetUploaderInput();
    this.uploadModal.hide();
  }

  private handle401Error(): void {
    this.auth.refreshToken().subscribe(
      (token: any) => {
        this.uploadFile();
      },
      () => {
        this.isUploading = false;
      }
    );
  }
}

